import { createPortal } from "react-dom";
import { memo } from "react";
import gsap from "gsap";

import { TMaskArgs, TWindowConfig, TMaskObject } from "@common/components/types";

/**
 * Custom hook to handle GSAP animations for a mask effect.
 *
 * @param {TMaskArgs} args - Argument object for initializing the mask.
 * @param {React.MutableRefObject<HTMLElement>} [args.targetRef] - Reference to the target element.
 * @returns {TMaskObject} Object containing:
 * - onPointerIn: Handler for pointer in event, it applies the mask.
 *    - Needs an argument object containing width, height, x, y, transform (optional), for example:
 *    - {
 *      - width: "20%", // String with a CSS unit string
 *      - height: "40vh", // String with a CSS unit string
 *      - x: "400px", // String with a CSS unit string
 *      - y: "20rem", // String with a CSS unit string
 *      - transform: "rotate(-10)", // String with SVG transform attribute
 *      -  https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/transform
 *    - }
 * - onPointerOut: Handler for pointer out event, it removes the mask. Takes:
 * - reveal: Reveal animation for the mask meant to be used on link click.
 *    - Optional argument object containing width, height, x, y, transform (optional) mask with animate to those attributes and then apply full mask to the target
 *    - Optional argument onReveal, function to be trigger at the end of reveal animation and before reset of mask
 *    - Optional argument duration
 * - SVGMask: Memoized SVG mask component.
 */

function useMaskGSAP(args: TMaskArgs): TMaskObject {
	const { targetRef } = args;
	const target = targetRef.current;

	function onPointerIn(args) {
		const rect = document.getElementById("mask-rect-element");
		if (!target || !rect) return;
		target.style.mask = "unset";
		target.style.mask = "url(#use-mask-gsap)";
		gsap.to(rect, {
			attr: args,
		});
	}

	function onPointerOut() {
		const rect = document.getElementById("mask-rect-element");
		if (!target || !rect) return;
		gsap.to(rect, {
			attr: {
				width: "0%",
				height: "0%",
			},
			onComplete: () => {
				target.style.mask = "unset";
			},
		});
	}

	function reveal(args: TWindowConfig | "default", onReveal, duration = 1) {
		const rect = document.getElementById("mask-rect-element");
		if (!target || !rect) return;
		if (args === "default") {
			gsap.to(rect, {
				attr: {
					width: "100%",
					height: "100%",
					x: "0%",
					y: "0%",
					transform: "",
					duration,
				},
				onComplete: () => {
					onReveal?.();
					gsap.set(rect, {
						attr: {
							width: "0%",
							height: "0%",
							x: "50%",
							y: "50%",
						},
					});
				},
			});
		} else {
			const tml = gsap.timeline();
			tml.to(
				rect,
				{
					attr: args,
				},
				0,
			);
		}
	}

	const SVGMask = memo(() => {
		return createPortal(
			<svg
				id="masking-svg-defs"
				width="100%"
				height="100%"
				viewBox={`0 0 ${innerWidth} ${innerHeight}`}
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					pointerEvents: "none",
				}}
			>
				<defs>
					<mask id="use-mask-gsap">
						<rect width="100%" height="100%" x="0" y="0" fill="white" />
						<rect
							id="mask-rect-element"
							width="0%"
							height="0%"
							x="50%"
							y="50%"
							fill="black"
							style={{
								transformOrigin: "center",
								transformBox: "fill-box",
							}}
						>
							<title>Mask Rectangle</title>
						</rect>
					</mask>
				</defs>
			</svg>,
			document.body,
		);
	});

	return {
		onPointerIn,
		onPointerOut,
		reveal,
		SVGMask,
	};
}

export default useMaskGSAP;
